import type { Map } from "maplibre-gl";
import {
  TerraDraw,
  TerraDrawFreehandMode,
  TerraDrawLineStringMode,
  TerraDrawPolyLineMode,
  TerraDrawPointMode,
  TerraDrawPolygonMode,
  TerraDrawRectangleMode,
  TerraDrawSelectMode,
  TerraDrawRenderMode,
  type GeoJSONStoreFeatures,
  type GeoJSONStoreGeometries
} from "terra-draw";
import { TerraDrawMapLibreGLAdapter } from "terra-draw-maplibre-gl-adapter";
import type { GeoJsonDocument, GeoJsonFeatureCollection, JsonValue, SimpleDrawingFeature } from "../../core/types/geojson";
import { closedLineToPolygonRing } from "../../core/utils/geometry";
import { GeoJsonBounds } from "../geojson/GeoJsonBounds";
import { GeoJsonParser } from "../geojson/GeoJsonParser";

export type DrawMode = "render" | "select" | "point" | "linestring" | "polyline" | "polygon" | "rectangle" | "freehand";

type TerraFeature = GeoJSONStoreFeatures<GeoJSONStoreGeometries>;
type TerraGeometryType = "Point" | "LineString" | "Polygon";

type TerraProperties = Record<string, JsonValue | undefined>;

export interface DrawingCallbacks {
  onChange?: (collection: GeoJsonFeatureCollection) => void;
  onSelect?: (feature: TerraFeature | null) => void;
  onFinish?: (collection: GeoJsonFeatureCollection) => void;
  onMessage?: (message: string, tone?: "info" | "success" | "error") => void;
}

const ORIGINAL_DEFAULTS = {
  polygon: {
    fillColor: "#555555",
    fillOpacity: 0.2,
    strokeColor: "#000000",
    strokeOpacity: 1,
    strokeWidth: 1
  },
  line: {
    strokeColor: "#555555",
    strokeOpacity: 1,
    strokeWidth: 2
  },
  point: {
    color: "#dc2626",
    width: 6
  }
} as const;

const ORIGINAL_COORDINATE_PRECISION = 20;

const INTERNAL_TERRA_KEYS = new Set([
  "mode",
  "currentlyDrawing",
  "edited",
  "closingPoint",
  "closingPointId",
  "snappingPoint",
  "coordinatePoint",
  "coordinatePointId",
  "coordinatePointIds",
  "provisionalCoordinateCount",
  "committedCoordinateCount",
  "marker",
  "selectionPoint",
  "selectionPointFeatureId",
  "midPoint",
  "selected"
]);

/**
 * Terra Draw requires internal feature ids and a properties.mode value. The
 * original Google implementation did not export either of those. This service
 * therefore adds Terra Draw metadata only inside the drawing store and strips it
 * again when generating the user-facing GeoJSON output.
 */
export class TerraDrawService {
  private draw: TerraDraw | null = null;
  private selectedId: string | number | null = null;
  private history: GeoJsonFeatureCollection[] = [];
  private historyIndex = -1;
  private restoringHistory = false;
  private historyTimer = 0;

  constructor(private readonly map: Map, private readonly callbacks: DrawingCallbacks = {}) {}

  start(): void {
    this.draw = new TerraDraw({
      adapter: new TerraDrawMapLibreGLAdapter({ map: this.map, coordinatePrecision: ORIGINAL_COORDINATE_PRECISION }),
      modes: [
        new TerraDrawRenderMode({ modeName: "render", styles: this.renderStyles() as never }),
        new TerraDrawSelectMode({
          styles: this.selectStyles() as never,
          flags: {
            point: { feature: { draggable: true } },
            linestring: { feature: { draggable: true, coordinates: { draggable: true, midpoints: true, deletable: true } } },
            polyline: { feature: { draggable: true, coordinates: { draggable: true, midpoints: true, deletable: true } } },
            polygon: { feature: { draggable: true, coordinates: { draggable: true, midpoints: true, deletable: true } } },
            rectangle: { feature: { draggable: true, coordinates: { draggable: true, midpoints: true, deletable: true } } }
          }
        }),
        new TerraDrawPointMode({ editable: true, styles: this.pointStyles() as never }),
        new TerraDrawLineStringMode({
          editable: true,
          showCoordinatePoints: true,
          keyEvents: { cancel: "Escape", finish: "Enter" },
          cursors: { start: "crosshair", close: "pointer", dragStart: "grabbing", dragEnd: "grab" },
          styles: this.lineStyles() as never
        }),
        new TerraDrawPolyLineMode({
          modeName: "polyline",
          keyEvents: { cancel: "Escape", finish: "Enter" },
          cursors: { start: "crosshair", close: "pointer" },
          styles: this.polylineStyles() as never
        }),
        new TerraDrawPolygonMode({
          editable: true,
          showCoordinatePoints: true,
          keyEvents: { cancel: "Escape", finish: "Enter" },
          cursors: { start: "crosshair", close: "pointer", dragStart: "grabbing", dragEnd: "grab" },
          styles: this.polygonStyles() as never
        }),
        new TerraDrawRectangleMode({ keyEvents: { cancel: "Escape", finish: "Enter" }, styles: this.rectangleStyles() as never }),
        new TerraDrawFreehandMode({
          modeName: "freehand",
          minDistance: 0.7,
          smoothing: 0.25,
          autoClose: true,
          preventPointsNearClose: true,
          drawInteraction: "click-drag",
          keyEvents: { cancel: "Escape", finish: "Enter" },
          styles: this.freehandPolygonStyles() as never
        })
      ]
    });

    this.draw.on("change", () => {
      const collection = this.getFeatureCollection();
      this.callbacks.onChange?.(collection);
      this.scheduleHistoryRecord(collection);
    });
    this.draw.on("finish", () => {
      this.normalizeFinishedFreehandAreas();
      this.normalizeFinishedPolylineModeFeatures();
      this.normalizeFinishedClosedPolylines();
      const collection = this.getFeatureCollection();
      this.callbacks.onFinish?.(collection);
      this.cancelScheduledHistoryRecord();
      this.recordHistory(collection);
    });
    this.draw.on("select", (id) => {
      this.selectedId = id;
      this.callbacks.onSelect?.(this.getSelectedFeature());
    });
    this.draw.on("deselect", () => {
      this.selectedId = null;
      this.callbacks.onSelect?.(null);
    });

    this.draw.start();
    this.draw.setMode("render");
    this.recordHistory(this.getFeatureCollection());
  }

  setMode(mode: DrawMode): void {
    this.ensureStarted().setMode(mode);
  }

  clear(): void {
    this.cancelScheduledHistoryRecord();
    this.ensureStarted().clear();
    this.selectedId = null;
    this.callbacks.onSelect?.(null);
    const collection = this.getFeatureCollection();
    this.callbacks.onChange?.(collection);
    this.recordHistory(collection);
  }

  undo(): boolean {
    this.cancelScheduledHistoryRecord();
    if (this.historyIndex <= 0) return false;
    this.historyIndex -= 1;
    this.restoreHistorySnapshot(this.history[this.historyIndex]);
    return true;
  }

  redo(): boolean {
    this.cancelScheduledHistoryRecord();
    if (this.historyIndex >= this.history.length - 1) return false;
    this.historyIndex += 1;
    this.restoreHistorySnapshot(this.history[this.historyIndex]);
    return true;
  }

  deleteSelected(): boolean {
    this.cancelScheduledHistoryRecord();
    if (this.selectedId === null) return false;
    this.ensureStarted().removeFeatures([this.selectedId]);
    this.selectedId = null;
    this.callbacks.onSelect?.(null);
    const collection = this.getFeatureCollection();
    this.callbacks.onChange?.(collection);
    this.recordHistory(collection);
    return true;
  }

  importGeoJson(document: GeoJsonDocument): { imported: number; rejected: number; reasons: string[] } {
    this.cancelScheduledHistoryRecord();
    const features = GeoJsonParser.flattenForDrawing(document).map((feature) => this.toTerraFeature(feature));
    const validations = this.ensureStarted().addFeatures(features);
    const rejectedValidations = validations.filter((validation) => validation.valid === false);
    const collection = this.getFeatureCollection();
    this.callbacks.onChange?.(collection);
    this.recordHistory(collection);
    const bounds = GeoJsonBounds.preferredViewportFromDocument(document);
    if (bounds) this.map.fitBounds(bounds, { padding: 70, maxZoom: 6.5, duration: 650 });
    return {
      imported: features.length - rejectedValidations.length,
      rejected: rejectedValidations.length,
      reasons: rejectedValidations.map((validation) => validation.reason ?? "Unknown validation error")
    };
  }

  fitToFeatures(maxZoom = 6.5): boolean {
    const collection = this.getFeatureCollection();
    const bounds = GeoJsonBounds.preferredViewportFromDocument(collection);
    if (!bounds) return false;
    this.map.fitBounds(bounds, { padding: 70, maxZoom, duration: 650 });
    return true;
  }

  updateSelectedProperties(properties: Record<string, JsonValue>): void {
    this.cancelScheduledHistoryRecord();
    if (this.selectedId === null) return;
    const current = this.getSelectedFeature();
    if (!current) return;
    const cleanedProperties = this.stripReservedInputProperties(properties);
    this.ensureStarted().updateFeatureProperties(this.selectedId, cleanedProperties);
    const collection = this.getFeatureCollection();
    this.callbacks.onChange?.(collection);
    this.callbacks.onSelect?.(this.getSelectedFeature());
    this.recordHistory(collection);
  }

  getSelectedFeature(): TerraFeature | null {
    if (this.selectedId === null) return null;
    return this.ensureStarted().getSnapshotFeature(this.selectedId) ?? null;
  }

  getSelectedApplicationFeature(): { geometryType: TerraGeometryType; properties: Record<string, JsonValue> } | null {
    const selected = this.getSelectedFeature();
    if (!selected || !this.isExportableUserFeature(selected)) return null;
    const geometryType = selected.geometry.type as TerraGeometryType;
    return {
      geometryType,
      properties: this.withOriginalExportDefaults(geometryType, this.stripInternalProperties(selected.properties as TerraProperties))
    };
  }

  getFeatureCollection(): GeoJsonFeatureCollection {
    const features = this.ensureStarted()
      .getSnapshot()
      .filter((feature) => this.isExportableUserFeature(feature))
      .map((feature) => ({
        type: "Feature" as const,
        properties: this.withOriginalExportDefaults(feature.geometry.type as TerraGeometryType, this.stripInternalProperties(feature.properties as TerraProperties)),
        geometry: feature.geometry as never
      }));

    return { type: "FeatureCollection", features };
  }

  stop(): void {
    this.cancelScheduledHistoryRecord();
    this.draw?.stop();
    this.draw = null;
    this.selectedId = null;
    this.history = [];
    this.historyIndex = -1;
  }

  private scheduleHistoryRecord(collection: GeoJsonFeatureCollection): void {
    if (this.restoringHistory) return;
    if (this.historyTimer) window.clearTimeout(this.historyTimer);
    const snapshot = structuredClone(collection) as GeoJsonFeatureCollection;
    this.historyTimer = window.setTimeout(() => {
      this.historyTimer = 0;
      this.recordHistory(snapshot);
    }, 500);
  }

  private cancelScheduledHistoryRecord(): void {
    if (this.historyTimer) window.clearTimeout(this.historyTimer);
    this.historyTimer = 0;
  }

  private recordHistory(collection: GeoJsonFeatureCollection): void {
    if (this.restoringHistory) return;
    const snapshot = structuredClone(collection) as GeoJsonFeatureCollection;
    const serialized = JSON.stringify(snapshot);
    const current = this.history[this.historyIndex];
    if (current && JSON.stringify(current) === serialized) return;
    this.history = this.history.slice(0, this.historyIndex + 1);
    this.history.push(snapshot);
    this.historyIndex = this.history.length - 1;
  }

  private restoreHistorySnapshot(collection: GeoJsonFeatureCollection): void {
    const draw = this.ensureStarted();
    this.restoringHistory = true;
    draw.clear();
    const features = collection.features
      .filter((feature): feature is SimpleDrawingFeature => Boolean(feature.geometry && ["Point", "LineString", "Polygon"].includes(feature.geometry.type)))
      .map((feature) => this.toTerraFeature(feature));
    if (features.length) draw.addFeatures(features);
    this.selectedId = null;
    this.restoringHistory = false;
    this.callbacks.onSelect?.(null);
    this.callbacks.onChange?.(this.getFeatureCollection());
  }

  /**
   * Terra Draw stores a freehand area as a Polygon whose internal mode remains
   * `freehand`. Select/Edit works more consistently when finished areas are
   * normalized back to normal polygon features. User-facing GeoJSON is unchanged.
   */
  private normalizeFinishedFreehandAreas(): void {
    const draw = this.ensureStarted();
    const snapshot = draw.getSnapshot();
    const hasFreehandArea = snapshot.some((feature) => feature.geometry?.type === "Polygon" && (feature.properties as TerraProperties | undefined)?.mode === "freehand");
    if (!hasFreehandArea) return;
    const collection = this.getFeatureCollection();
    this.restoreHistorySnapshot(collection);
  }

  /**
   * TerraDrawPolyLineMode already implements the official UX where clicking
   * back on the first point closes the geometry as a Polygon. After finishing,
   * normalize its internal `polyline` mode into the standard geometry mode so
   * Select/Edit uses the same vertex/midpoint behavior as imported polygons
   * and open lines.
   */
  private normalizeFinishedPolylineModeFeatures(): void {
    const draw = this.ensureStarted();
    const snapshot = draw.getSnapshot();
    const hasPolylineModeFeature = snapshot.some((feature) => (feature.properties as TerraProperties | undefined)?.mode === "polyline");
    if (!hasPolylineModeFeature) return;

    const collection = this.getFeatureCollection();
    const polygonCount = collection.features.filter((feature) => feature.geometry?.type === "Polygon").length;
    this.restoreHistorySnapshot(collection);
    if (polygonCount > 0) {
      this.callbacks.onMessage?.("Closed boundary saved as a filled polygon region. Use Select/Edit to adjust points.", "success");
    }
  }

  /**
   * Terra Draw's line mode exposes a closing point. When the user creates a
   * polyline and connects the last point back to the first point, the expected
   * research workflow is an area, not a plain line. Convert that closed line
   * into a normal Polygon so it receives transparent fill styling and can be
   * edited/exported as a region.
   */
  private normalizeFinishedClosedPolylines(): void {
    const current = this.getFeatureCollection();
    let convertedCount = 0;

    const features = current.features.map((feature) => {
      if (feature.geometry?.type !== "LineString") return feature;
      const ring = closedLineToPolygonRing(feature.geometry.coordinates);
      if (!ring) return feature;
      convertedCount += 1;
      const properties = this.withOriginalExportDefaults("Polygon", { ...(feature.properties as Record<string, JsonValue>) });
      return {
        type: "Feature" as const,
        properties,
        geometry: {
          type: "Polygon" as const,
          coordinates: [ring]
        }
      };
    });

    if (convertedCount > 0) {
      this.restoreHistorySnapshot({ type: "FeatureCollection", features: features as never });
      this.callbacks.onMessage?.(`${convertedCount} closed polyline area saved as filled polygon region.`, "success");
    }
  }

  private ensureStarted(): TerraDraw {
    if (!this.draw) throw new Error("Terra Draw has not been started.");
    return this.draw;
  }

  private toTerraFeature(feature: SimpleDrawingFeature): TerraFeature {
    const geometryType = feature.geometry.type as TerraGeometryType;
    const mode = this.getModeForGeometry(geometryType);
    return {
      type: "Feature",
      id: this.ensureStarted().getFeatureId(),
      properties: {
        ...feature.properties,
        mode
      },
      geometry: feature.geometry as GeoJSONStoreGeometries
    };
  }

  private getModeForGeometry(geometryType: TerraGeometryType, existingMode?: string): DrawMode {
    if (existingMode === "rectangle") return "rectangle";
    if (geometryType === "Point") return "point";
    if (geometryType === "LineString") return "linestring";
    return "polygon";
  }

  private isExportableUserFeature(feature: TerraFeature): boolean {
    if (!feature.geometry || !["Point", "LineString", "Polygon"].includes(feature.geometry.type)) return false;
    const properties = feature.properties as TerraProperties;
    if (properties.coordinatePoint || properties.closingPoint || properties.snappingPoint || properties.selectionPoint || properties.midPoint) return false;
    if (properties.currentlyDrawing) return false;
    return !String(properties.mode ?? "").includes("coordinate") && !String(properties.mode ?? "").includes("midpoint");
  }

  private stripReservedInputProperties(properties: Record<string, JsonValue>): Record<string, JsonValue> {
    const result: Record<string, JsonValue> = {};
    for (const [key, value] of Object.entries(properties)) {
      if (INTERNAL_TERRA_KEYS.has(key) || key.startsWith("__poc")) continue;
      result[key] = value;
    }
    return result;
  }

  private stripInternalProperties(properties: TerraProperties): Record<string, JsonValue> {
    const result: Record<string, JsonValue> = {};
    for (const [key, value] of Object.entries(properties)) {
      if (INTERNAL_TERRA_KEYS.has(key) || key.startsWith("__poc")) continue;
      if (value === undefined) continue;
      if (this.isJsonValue(value)) result[key] = value;
    }
    return result;
  }

  private withOriginalExportDefaults(geometryType: TerraGeometryType, properties: Record<string, JsonValue>): Record<string, JsonValue> {
    const result = { ...properties };
    if (!Object.hasOwn(result, "name")) result.name = "";

    if (geometryType === "Polygon") {
      if (!Object.hasOwn(result, "strokeColor") && !Object.hasOwn(result, "stroke")) result.strokeColor = ORIGINAL_DEFAULTS.polygon.strokeColor;
      if (!Object.hasOwn(result, "fillColor") && !Object.hasOwn(result, "fill")) result.fillColor = ORIGINAL_DEFAULTS.polygon.fillColor;
    }

    if (geometryType === "LineString") {
      if (!Object.hasOwn(result, "strokeColor") && !Object.hasOwn(result, "stroke")) result.strokeColor = ORIGINAL_DEFAULTS.line.strokeColor;
    }

    return result;
  }

  private isJsonValue(value: unknown): value is JsonValue {
    if (value === null) return true;
    if (["string", "number", "boolean"].includes(typeof value)) return true;
    if (Array.isArray(value)) return value.every((item) => this.isJsonValue(item));
    if (typeof value === "object") return Object.values(value as Record<string, unknown>).every((item) => this.isJsonValue(item));
    return false;
  }

  private renderStyles() {
    return {
      polygonFillColor: (feature: TerraFeature) => this.polygonFillColor(feature),
      polygonFillOpacity: (feature: TerraFeature) => this.numberProp(feature, "fillOpacity", ORIGINAL_DEFAULTS.polygon.fillOpacity),
      polygonOutlineColor: (feature: TerraFeature) => this.polygonStrokeColor(feature),
      polygonOutlineWidth: (feature: TerraFeature) => this.numberPropAny(feature, ["strokeWidth", "strokeWeight"], ORIGINAL_DEFAULTS.polygon.strokeWidth),
      lineStringColor: (feature: TerraFeature) => this.lineStrokeColor(feature),
      lineStringWidth: (feature: TerraFeature) => this.numberPropAny(feature, ["strokeWidth", "strokeWeight"], ORIGINAL_DEFAULTS.line.strokeWidth),
      lineStringOpacity: (feature: TerraFeature) => this.numberProp(feature, "strokeOpacity", ORIGINAL_DEFAULTS.line.strokeOpacity),
      pointColor: (feature: TerraFeature) => this.colorProp(feature, ["color", "fillColor", "fill"], ORIGINAL_DEFAULTS.point.color),
      pointWidth: (feature: TerraFeature) => this.numberProp(feature, "radius", ORIGINAL_DEFAULTS.point.width),
      zIndex: (feature: TerraFeature) => this.numberProp(feature, "zIndex", 0)
    };
  }

  private polygonStyles() {
    return {
      fillColor: (feature: TerraFeature) => this.polygonFillColor(feature),
      fillOpacity: (feature: TerraFeature) => this.numberProp(feature, "fillOpacity", ORIGINAL_DEFAULTS.polygon.fillOpacity),
      outlineColor: (feature: TerraFeature) => this.polygonStrokeColor(feature),
      outlineOpacity: (feature: TerraFeature) => this.numberProp(feature, "strokeOpacity", ORIGINAL_DEFAULTS.polygon.strokeOpacity),
      outlineWidth: (feature: TerraFeature) => this.numberPropAny(feature, ["strokeWidth", "strokeWeight"], ORIGINAL_DEFAULTS.polygon.strokeWidth),
      coordinatePointColor: "#111827",
      coordinatePointWidth: 4,
      closingPointColor: "#111827",
      closingPointWidth: 5
    };
  }

  private rectangleStyles() {
    return {
      fillColor: (feature: TerraFeature) => this.polygonFillColor(feature),
      fillOpacity: (feature: TerraFeature) => this.numberProp(feature, "fillOpacity", ORIGINAL_DEFAULTS.polygon.fillOpacity),
      outlineColor: (feature: TerraFeature) => this.polygonStrokeColor(feature),
      outlineOpacity: (feature: TerraFeature) => this.numberProp(feature, "strokeOpacity", ORIGINAL_DEFAULTS.polygon.strokeOpacity),
      outlineWidth: (feature: TerraFeature) => this.numberPropAny(feature, ["strokeWidth", "strokeWeight"], ORIGINAL_DEFAULTS.polygon.strokeWidth)
    };
  }

  private lineStyles() {
    return {
      lineStringColor: (feature: TerraFeature) => this.lineStrokeColor(feature),
      lineStringWidth: (feature: TerraFeature) => this.numberPropAny(feature, ["strokeWidth", "strokeWeight"], ORIGINAL_DEFAULTS.line.strokeWidth),
      lineStringOpacity: (feature: TerraFeature) => this.numberProp(feature, "strokeOpacity", ORIGINAL_DEFAULTS.line.strokeOpacity),
      coordinatePointColor: "#111827",
      coordinatePointWidth: 4,
      closingPointColor: "#111827",
      closingPointWidth: 7,
      closingPointOpacity: 1,
      closingPointOutlineColor: "#ffffff",
      closingPointOutlineWidth: 2,
      closingPointOutlineOpacity: 1,
      snappingPointColor: "#2563eb",
      snappingPointWidth: 6,
      snappingPointOpacity: 1,
      snappingPointOutlineColor: "#ffffff",
      snappingPointOutlineWidth: 2,
      snappingPointOutlineOpacity: 1
    };
  }

  private polylineStyles() {
    return {
      lineStringColor: (feature: TerraFeature) => this.lineStrokeColor(feature),
      lineStringWidth: (feature: TerraFeature) => this.numberPropAny(feature, ["strokeWidth", "strokeWeight"], ORIGINAL_DEFAULTS.line.strokeWidth),
      lineStringOpacity: (feature: TerraFeature) => this.numberProp(feature, "strokeOpacity", ORIGINAL_DEFAULTS.line.strokeOpacity),
      polygonFillColor: (feature: TerraFeature) => this.polygonFillColor(feature),
      polygonFillOpacity: (feature: TerraFeature) => this.numberProp(feature, "fillOpacity", ORIGINAL_DEFAULTS.polygon.fillOpacity),
      polygonOutlineColor: (feature: TerraFeature) => this.polygonStrokeColor(feature),
      polygonOutlineWidth: (feature: TerraFeature) => this.numberPropAny(feature, ["strokeWidth", "strokeWeight"], ORIGINAL_DEFAULTS.polygon.strokeWidth),
      polygonOutlineOpacity: (feature: TerraFeature) => this.numberProp(feature, "strokeOpacity", ORIGINAL_DEFAULTS.polygon.strokeOpacity),
      closingPointColor: "#111827",
      closingPointWidth: 8,
      closingPointOpacity: 1,
      closingPointOutlineColor: "#ffffff",
      closingPointOutlineWidth: 2,
      closingPointOutlineOpacity: 1,
      snappingPointColor: "#2563eb",
      snappingPointWidth: 6,
      snappingPointOpacity: 1,
      snappingPointOutlineColor: "#ffffff",
      snappingPointOutlineWidth: 2,
      snappingPointOutlineOpacity: 1
    };
  }

  private pointStyles() {
    return {
      pointColor: (feature: TerraFeature) => this.colorProp(feature, ["color", "fillColor", "fill"], ORIGINAL_DEFAULTS.point.color),
      pointWidth: (feature: TerraFeature) => this.numberProp(feature, "radius", ORIGINAL_DEFAULTS.point.width),
      pointOutlineColor: "#ffffff",
      pointOutlineWidth: 1.5
    };
  }

  private freehandPolygonStyles() {
    return {
      fillColor: (feature: TerraFeature) => this.polygonFillColor(feature),
      fillOpacity: (feature: TerraFeature) => this.numberProp(feature, "fillOpacity", ORIGINAL_DEFAULTS.polygon.fillOpacity),
      outlineColor: (feature: TerraFeature) => this.polygonStrokeColor(feature),
      outlineOpacity: (feature: TerraFeature) => this.numberProp(feature, "strokeOpacity", ORIGINAL_DEFAULTS.polygon.strokeOpacity),
      outlineWidth: (feature: TerraFeature) => this.numberPropAny(feature, ["strokeWidth", "strokeWeight"], ORIGINAL_DEFAULTS.polygon.strokeWidth),
      closingPointColor: "#111827",
      closingPointOpacity: 1,
      closingPointWidth: 5,
      closingPointOutlineColor: "#ffffff",
      closingPointOutlineOpacity: 1,
      closingPointOutlineWidth: 1.5
    };
  }

  private selectStyles() {
    return {
      selectedPolygonColor: (feature: TerraFeature) => this.polygonFillColor(feature),
      selectedPolygonFillOpacity: 0.35,
      selectedPolygonOutlineColor: "#111827",
      selectedPolygonOutlineWidth: 2,
      selectedLineStringColor: "#111827",
      selectedLineStringWidth: 3,
      selectedPointColor: "#111827",
      selectedPointWidth: 8,
      selectionPointColor: "#111827",
      selectionPointWidth: 5,
      midPointColor: "#ffffff",
      midPointOutlineColor: "#111827",
      midPointWidth: 4
    };
  }

  private polygonFillColor(feature: TerraFeature): string {
    return this.colorProp(feature, ["fillColor", "fill"], ORIGINAL_DEFAULTS.polygon.fillColor);
  }

  private polygonStrokeColor(feature: TerraFeature): string {
    return this.colorProp(feature, ["strokeColor", "stroke"], ORIGINAL_DEFAULTS.polygon.strokeColor);
  }

  private lineStrokeColor(feature: TerraFeature): string {
    return this.colorProp(feature, ["strokeColor", "stroke"], ORIGINAL_DEFAULTS.line.strokeColor);
  }

  private colorProp(feature: TerraFeature, keys: string[], fallback: string): string {
    const properties = feature.properties as TerraProperties;
    for (const key of keys) {
      const value = properties[key];
      if (typeof value === "string" && value.trim()) return value;
    }
    const icon = properties.icon;
    if (icon && typeof icon === "object" && !Array.isArray(icon)) {
      const iconColor = (icon as Record<string, JsonValue>).fillColor ?? (icon as Record<string, JsonValue>).strokeColor;
      if (typeof iconColor === "string" && iconColor.trim()) return iconColor;
    }
    return fallback;
  }

  private numberPropAny(feature: TerraFeature, keys: string[], fallback: number): number {
    for (const key of keys) {
      const value = this.numberProp(feature, key, Number.NaN);
      if (Number.isFinite(value)) return value;
    }
    return fallback;
  }

  private numberProp(feature: TerraFeature, key: string, fallback: number): number {
    const value = (feature.properties as TerraProperties)[key];
    if (typeof value === "number" && Number.isFinite(value)) return value;
    if (typeof value === "string" && value.trim() && Number.isFinite(Number(value))) return Number(value);
    return fallback;
  }
}
